const jsPDF = require('jspdf')
require('jspdf-autotable')
const { headerFromUser } = require('./util/pdfDocument')
const { getStyle } = require('./reportStyle')
const { getUserPartner } = require('fleetmap-partners')
const {
  convertToLocaleString, getTranslations,
  getLanguage
} = require('./util/utils')
const traccarHelper = require('./util/traccar')
const { devicesToProcess } = require('./util/device')
const automaticReports = require('./automaticReports')
const { getDriverName, getDriverData } = require('./util/driver')

const fileName = 'LocationReport'

async function createLocationReport (from, to, userData, traccar) {
  console.log('Create LocationReport')
  const reportData = []

  if (userData.byDriver) {
    console.log('ByDriver')
    const report = await createLocationReportByDriver(from, to, userData, traccar)
    reportData.push(report)
  } else if (userData.byGroup) {
    console.log('ByGroup')
    const allData = await createLocationReportByGroup(from, to, userData, traccar)
    reportData.push(...allData)
  } else {
    console.log('ByDevice')
    const report = await createLocationReportByDevice(from, to, userData, traccar)
    reportData.push(report)
  }

  return reportData
}

async function createLocationReportByDriver (from, to, userData, traccar) {
  const devices = await traccar.devices.devicesGet().then(d => d.data)
  console.log(devices.length)

  if (!devices.length) {
    // Empty report
    return { drivers: [] }
  }

  const allInOne = await traccarHelper.getAllInOne(traccar, from, to, devices, true, false, false, false)

  return { drivers: await processDrivers(from, to, userData, allInOne) }
}

async function createLocationReportByGroup (from, to, userData, traccar) {
  const reportData = []

  for (const g of userData.groups) {
    const devices = userData.devices.filter(d => d.groupId === g.id)

    if (devices.length > 0) {
      const groupData = {
        devices: [],
        group: g,
        xpert: devices.filter(d => d.attributes.xpert).length > 0
      }

      const response = await traccar.reports.reportsRouteGet(from, to, null, [g.id])
      const data = response.data

      devices.sort((a, b) => (a.name > b.name) ? 1 : -1)

      if (data.length > 0) {
        console.log('Locations:' + data.length)
        groupData.devices = processDevices(from, to, devices, userData.drivers, data)

        reportData.push(groupData)
      }
    }
  }

  const allData = await createLocationReportByDevice(from, to, userData, traccar)
  reportData.push(allData)

  return reportData
}

async function createLocationReportByDevice (from, to, userData, traccar) {
  const devices = devicesToProcess(userData)

  const allData = {
    devices: [],
    xpert: devices.filter(d => d.attributes.xpert).length > 0
  }

  const sliced = automaticReports.sliceArray(devices, 5)

  let deviceCount = 0
  for (const slice of sliced) {
    const allInOne = await traccarHelper.getAllInOne(traccar, from, to, slice, true, false, false, false, deviceCount, devices.length)
    const routeData = allInOne.route

    console.log('Locations:' + routeData.length)

    if (routeData.length > 0) {
      allData.devices.push(...processDevices(from, to, slice, {
        route: routeData
      }, userData))
    }
    deviceCount = deviceCount + slice.length
  }

  return allData
}

function processDevices (from, to, devices, data) {
  const devicesResult = []

  for (const d of devices) {
    const positions = data.route.filter(t => t.deviceId === d.id)

    const lighterPositions = positions.map(p => {
      return {
        id: p.id,
        fixTime: p.fixTime,
        address: p.address,
        speed: p.speed,
        latitude: p.latitude,
        longitude: p.longitude,
        attributes: {
          driverUniqueId: p.attributes.driverUniqueId,
          temp1: p.attributes.temp1,
          temp2: p.attributes.temp2,
          door1: p.attributes.door1,
          door2: p.attributes.door2,
          sensor: p.attributes.sensor,
          rain: p.attributes.rain,
          ignition: p.attributes.ignition,
          alarm: p.attributes.alarm,
          input: p.attributes.input,
          di2: p.attributes.di2,
          di3: p.attributes.di3
        }
      }
    })
    if (lighterPositions.length > 0) {
      devicesResult.push({
        device: d,
        from,
        to,
        positions: lighterPositions
      })
    }
  }

  return devicesResult
}

async function processDrivers (from, to, userData, data) {
  const driversResult = []
  for (const d of userData.drivers) {
    const { route } = await getDriverData(d, data, userData)
    if (route.length > 0) {
      route.sort((a, b) => new Date(a.fixTime).getTime() - new Date(b.fixTime).getTime())

      route.forEach(p => {
        const device = userData.devices.find(d => d.id === p.deviceId)
        if (device) {
          p.vehicleName = device.name
        }
      })

      const driverData = {
        driver: d,
        from,
        to,
        positions: route
      }
      driversResult.push(driverData)
    }
  }
  console.log(driversResult)

  return driversResult
}

async function exportLocationReportToPDF (userData, reportData) {
  console.log('Export to PDF')

  const translations = getTranslations(userData)
  const lang = getLanguage(userData)
  const timezone = userData.user.attributes.timezone

  const positionsData = userData.byDriver ? reportData.drivers : reportData.devices

  const headers = userData.byDriver
    ? [
        translations.report.date,
        translations.report.address,
        translations.report.speed,
        translations.report.ignition,
        translations.report.vehicle
      ]
    : [
        translations.report.date,
        translations.report.address,
        translations.report.speed,
        translations.report.ignition,
        translations.report.driver
      ]

  if (userData.includeTemperature) {
    headers.push(translations.report.temperature)
    headers.push(translations.report.temperature2 || 'Temperature 2')
  }

  if (positionsData) {
    if (userData.includeDigitalPorts) {
      headers.push(
        getDigitalPort1Label(
          userData.byDriver ? positionsData[0].driver : positionsData[0].device,
          translations,
          positionsData[0].device.attributes
        ))
      headers.push(
        getDigitalPort2Label(
          userData.byDriver ? positionsData[0].driver : positionsData[0].device,
          translations,
          positionsData[0].device.attributes))
    }
    headers.push('Latitude')
    headers.push('Longitude')

    let first = true
    // eslint-disable-next-line new-cap
    const doc = new jsPDF.jsPDF('l')
    await headerFromUser(doc, translations.report.titleLocationReport, userData.user)

    positionsData.forEach(d => {
      const data = []
      const name = userData.byDriver ? d.driver.name : deviceName(d.device)
      const group = userData.byDriver ? '' : deviceGroupName(userData.groups, d.device, translations)

      let space = 0
      if (!first) {
        doc.addPage()
      } else {
        first = false
        space = 10
      }
      doc.setFontSize(13)
      doc.text(name, 20, space + 20)
      doc.setFontSize(11)
      doc.text(group, 200, space + 20)
      doc.text(convertToLocaleString(d.from, lang, timezone) + ' - ' + convertToLocaleString(d.to, lang, timezone), 20, space + 25)
      d.positions.forEach(a => {
        const temp = [
          getLocationDate(a, userData.user),
          a.address,
          Math.round(a.speed * 1.85200),
          a.attributes.ignition ? translations.report.ignitionOn : translations.report.ignitionOff,
          userData.byDriver ? a.vehicleName : getDriverName(a.attributes.driverUniqueId, a.fixTime, userData)
        ]

        if (userData.includeTemperature) {
          temp.push(getTempValue(a, 'temp1'))
          temp.push(getTempValue(a, 'temp2'))
        }

        if (userData.includeDigitalPorts) {
          temp.push(getDigitalPortValue(a, 1, translations, d.device.attributes))
          temp.push(getDigitalPortValue(a, 2, translations, d.device.attributes))
        }
        temp.push(a.latitude)
        temp.push(a.longitude)
        data.push(temp)
      })

      const footValues = [
        'Total:' + d.positions.length,
        '', getMaxSpeed(d.positions)
      ]

      const style = getStyle(getUserPartner(userData.user))
      doc.autoTable({
        head: [headers],
        body: data,
        foot: [footValues],
        showFoot: 'lastPage',
        headStyles: {
          fillColor: style.pdfHeaderColor,
          textColor: style.pdfHeaderTextColor,
          fontSize: 10
        },
        bodyStyles: {
          fillColor: style.pdfBodyColor,
          textColor: style.pdfBodyTextColor,
          fontSize: 8
        },
        footStyles: {
          fillColor: style.pdfFooterColor,
          textColor: style.pdfFooterTextColor,
          fontSize: 9
        },
        startY: space + 35
      })
    })

    return doc
  }
}

function getTempValue (row, sensor) {
  return (row.attributes[sensor] !== 175 && row.attributes[sensor]) || ''
}

function getDigitalPort1Label (row, translations, dAttributes) {
  return (dAttributes && dAttributes.sensor1) || (row.attributes && row.attributes.sensor1) || translations.report.digital_ports1
}

function getDigitalPort2Label (row, translations, dAttributes) {
  return (dAttributes && dAttributes.sensor2) || (row.attributes && row.attributes.sensor2) || translations.report.digital_ports2
}

function getDigitalPortValue (row, index, translations, dAttributes) {
  switch (index) {
    case 1:
      if (row.attributes && row.attributes.door1 !== undefined) {
        return translations.report[row.attributes.door1]
      }
      if (row.attributes && row.attributes.sensor !== undefined) {
        return row.attributes.sensor
          ? dAttributes.sensor1on || row.attributes.sensor
          : dAttributes.sensor1off || row.attributes.sensor
      }
      if (row.attributes && row.attributes[dAttributes.sensor1Attribute] !== undefined) {
        return row.attributes[dAttributes.sensor1Attribute]
          ? dAttributes.sensor1on || row.attributes.sensor
          : dAttributes.sensor1off || row.attributes.sensor
      }
      break
    case 2:
      if (row.attributes && row.attributes[dAttributes.sensor2Attribute] !== undefined) {
        return row.attributes[dAttributes.sensor2Attribute]
          ? dAttributes.sensor2on || row.attributes.sensor
          : dAttributes.sensor2off || row.attributes.sensor
      }
      if (row.attributes && row.attributes.door2) {
        return translations.report[row.attributes.door2]
      }
      return row.attributes && row.attributes.rain ? translations.report[row.attributes.rain] : ''
    default:
      return ''
  }
}

function exportLocationReportToExcel (userData, reportData) {
  console.log('Export to Excel')

  const translations = getTranslations(userData)

  const positionsData = userData.byDriver ? reportData.drivers : reportData.devices

  const settings = {
    sheetName: translations.report.titleLocationReport, // The name of the sheet
    fileName // The name of the spreadsheet
  }
  const headers = userData.byDriver
    ? [
        { label: translations.report.driver, value: 'driver' },
        { label: translations.report.date, value: 'date' },
        { label: translations.report.address, value: 'address' },
        { label: translations.report.speed, value: 'speed' },
        { label: translations.report.ignition, value: 'ignition' },
        { label: translations.report.vehicle, value: 'name' },
        { label: translations.report.temperature, value: 'temperature' }
      ]
    : [
        { label: translations.report.vehicle, value: 'name' },
        { label: translations.report.group, value: 'group' },
        { label: translations.report.date, value: 'date' },
        { label: translations.report.address, value: 'address' },
        { label: translations.report.speed, value: 'speed' },
        { label: translations.report.ignition, value: 'ignition' },
        { label: translations.report.driver, value: 'driver' }
      ]

  if (userData.includeTemperature) {
    headers.push({ label: translations.report.temperature, value: 'temperature' })
    headers.push({ label: translations.report.temperature2, value: 'temperature2' })
  }

  headers.push({ label: 'Latitude', value: 'latitude' })
  headers.push({ label: 'Longitude', value: 'longitude' })

  let data = []
  if (positionsData) {
    if (userData.includeDigitalPorts) {
      headers.push({ label: getDigitalPort1Label(userData.byDriver ? positionsData[0].driver : positionsData[0].device, translations), value: 'digitalport1' })
      headers.push({ label: getDigitalPort2Label(userData.byDriver ? positionsData[0].driver : positionsData[0].device, translations), value: 'digitalport2' })
    }

    positionsData.forEach(d => {
      data = data.concat(d.positions.map(a => {
        return {
          name: userData.byDriver ? a.vehicleName : d.device.name,
          group: userData.byDriver ? '' : deviceGroupName(userData.groups, d.device),
          date: getLocationDate(a, userData.user),
          address: a.address,
          speed: Math.round(a.speed * 1.85200),
          ignition: a.attributes.ignition ? translations.report.ignitionOn : translations.report.ignitionOff,
          driver: userData.byDriver ? d.driver.name : getDriverName(a.attributes.driverUniqueId, a.fixTime, userData),
          temperature: getTempValue(a, 'temp1'),
          temperature2: getTempValue(a, 'temp2'),
          digitalport1: d.device && getDigitalPortValue(a, 1, translations, d.device.attributes),
          digitalport2: d.device && getDigitalPortValue(a, 2, translations, d.device.attributes),
          latitude: a.latitude,
          longitude: a.longitude
        }
      }))
    })
    return {
      headers,
      data,
      settings
    }
  }
}

function deviceGroupName (groups, device, translation) {
  const group = groups.find(g => device.groupId === g.id)
  if (group) {
    if (translation) {
      return translation.report.group + ': ' + group.name
    } else {
      return group.name
    }
  }
  return ''
}

function deviceName (device) {
  return device.name + (device.attributes.license_plate ? ', ' + device.attributes.license_plate : '') + (device.model ? ', ' + device.model : '')
}

function getLocationDate (location, user) {
  return convertToLocaleString(location.fixTime, user.attributes.lang, user.attributes.timezone)
}

function getMaxSpeed (data) {
  const values = data.map(item => {
    return item.speed
  })
  const max = values.reduce(function (a, b) {
    return Math.max(a, b)
  })
  return Math.round(max * 1.85200)
}

exports.createLocationReport = createLocationReport
exports.exportLocationReportToPDF = exportLocationReportToPDF
exports.exportLocationReportToExcel = exportLocationReportToExcel
exports.create = createLocationReport
exports.exportToPDF = exportLocationReportToPDF
exports.exportToExcel = exportLocationReportToExcel
exports.getDigitalPortValue = getDigitalPortValue
